import { supabase } from './supabase';

export interface TeamMember {
  id: string;
  full_name: string | null;
  email: string;
  job_title: string | null;
  avatar_url: string | null;
  role: string;
  joined_at: string;
  requests_count: number;
  completed_count: number;
  pending_count: number;
  response_rate: number;
}

export interface TeamMetrics {
  total_members: number;
  total_requests: number;
  completed_requests: number;
  pending_requests: number;
  average_response_rate: number;
  active_cycles: number;
}

export interface FeedbackCycle {
  id: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  created_by: string;
  created_at: string;
  participant_count: number;
  completed_count: number;
}

export async function checkManagerAccess(userId: string): Promise<{
  hasAccess: boolean;
  organization: any;
  role: string | null;
}> {
  const { data: membership, error } = await supabase
    .from('organization_members')
    .select('role, organization_id, organizations(*)')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !membership) {
    return { hasAccess: false, organization: null, role: null };
  }

  const hasAccess = ['owner', 'admin', 'manager'].includes(membership.role);

  return {
    hasAccess,
    organization: membership.organizations,
    role: membership.role
  };
}

export async function getTeamMembers(organizationId: string): Promise<TeamMember[]> {
  const { data: members, error } = await supabase
    .from('organization_members')
    .select(`
      id,
      role,
      joined_at,
      user_id,
      profiles:user_id (
        id,
        full_name,
        email,
        job_title,
        avatar_url
      )
    `)
    .eq('organization_id', organizationId)
    .order('joined_at', { ascending: false });

  if (error) throw error;

  const teamMembers = await Promise.all(
    (members || []).map(async (member: any) => {
      const profile = member.profiles;

      const { count: totalRequests } = await supabase
        .from('feedback_requests')
        .select('*', { count: 'exact', head: true })
        .eq('requester_id', profile.id);

      const { count: completedRequests } = await supabase
        .from('feedback_requests')
        .select('*', { count: 'exact', head: true })
        .eq('requester_id', profile.id)
        .eq('status', 'completed');

      const { count: pendingRequests } = await supabase
        .from('feedback_requests')
        .select('*', { count: 'exact', head: true })
        .eq('requester_id', profile.id)
        .eq('status', 'pending');

      const total = totalRequests || 0;
      const completed = completedRequests || 0;
      const pending = pendingRequests || 0;
      const responseRate = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        id: profile.id,
        full_name: profile.full_name,
        email: profile.email,
        job_title: profile.job_title,
        avatar_url: profile.avatar_url,
        role: member.role,
        joined_at: member.joined_at,
        requests_count: total,
        completed_count: completed,
        pending_count: pending,
        response_rate: responseRate
      };
    })
  );

  return teamMembers;
}

export async function getTeamMetrics(organizationId: string): Promise<TeamMetrics> {
  const { data: memberIds } = await supabase
    .from('organization_members')
    .select('user_id')
    .eq('organization_id', organizationId);

  if (!memberIds || memberIds.length === 0) {
    return {
      total_members: 0,
      total_requests: 0,
      completed_requests: 0,
      pending_requests: 0,
      average_response_rate: 0,
      active_cycles: 0
    };
  }

  const userIds = memberIds.map(m => m.user_id);

  const { count: totalRequests } = await supabase
    .from('feedback_requests')
    .select('*', { count: 'exact', head: true })
    .in('requester_id', userIds);

  const { count: completedRequests } = await supabase
    .from('feedback_requests')
    .select('*', { count: 'exact', head: true })
    .in('requester_id', userIds)
    .eq('status', 'completed');

  const { count: pendingRequests } = await supabase
    .from('feedback_requests')
    .select('*', { count: 'exact', head: true })
    .in('requester_id', userIds)
    .eq('status', 'pending');

  const { count: activeCycles } = await supabase
    .from('feedback_cycles')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .eq('status', 'active');

  const total = totalRequests || 0;
  const completed = completedRequests || 0;
  const averageResponseRate = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    total_members: memberIds.length,
    total_requests: total,
    completed_requests: completed,
    pending_requests: pendingRequests || 0,
    average_response_rate: averageResponseRate,
    active_cycles: activeCycles || 0
  };
}

export async function getMemberFeedbackRequests(userId: string) {
  const { data, error } = await supabase
    .from('feedback_requests')
    .select('*')
    .eq('requester_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getMemberFeedbackSummary(userId: string) {
  const { data, error } = await supabase
    .from('feedback_summaries')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

export async function getFeedbackCycles(organizationId: string): Promise<FeedbackCycle[]> {
  const { data: cycles, error } = await supabase
    .from('feedback_cycles')
    .select(`
      *,
      participant_count:cycle_participants(count),
      completed_count:cycle_participants(count).eq(status,completed)
    `)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (cycles || []).map((cycle: any) => ({
    id: cycle.id,
    name: cycle.name,
    description: cycle.description,
    status: cycle.status,
    start_date: cycle.start_date,
    end_date: cycle.end_date,
    created_by: cycle.created_by,
    created_at: cycle.created_at,
    participant_count: cycle.participant_count?.[0]?.count || 0,
    completed_count: cycle.completed_count?.[0]?.count || 0
  }));
}

export async function createFeedbackCycle(
  organizationId: string,
  createdBy: string,
  name: string,
  description: string,
  startDate: string | null,
  endDate: string | null
) {
  const { data, error } = await supabase
    .from('feedback_cycles')
    .insert({
      organization_id: organizationId,
      created_by: createdBy,
      name,
      description,
      start_date: startDate,
      end_date: endDate,
      status: 'draft'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function addCycleParticipants(
  cycleId: string,
  participantIds: string[],
  role: 'recipient' | 'reviewer' | 'both' = 'recipient'
) {
  const participants = participantIds.map(id => ({
    cycle_id: cycleId,
    participant_id: id,
    role
  }));

  const { error } = await supabase
    .from('cycle_participants')
    .insert(participants);

  if (error) throw error;
}

export async function updateCycleStatus(cycleId: string, status: string) {
  const { error } = await supabase
    .from('feedback_cycles')
    .update({ status })
    .eq('id', cycleId);

  if (error) throw error;
}

export async function logManagerAction(
  userId: string,
  organizationId: string,
  action: string,
  resourceType: string,
  resourceId: string | null,
  metadata: any = {}
) {
  await supabase
    .from('audit_logs')
    .insert({
      user_id: userId,
      organization_id: organizationId,
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      metadata
    });
}
